import React, { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { CheckCircle, AlertTriangle, Mail, Lock, Loader } from 'lucide-react'
import { auth } from '../utils/firebase'
import './AuthActionPage.css'

/**
 * Branded landing page that handles every email-link action Firebase can
 * send: email verification, email-change verification, email-change
 * revocation, and password reset. Replaces Firebase's default white
 * action handler page.
 *
 * The action URLs configured in Firebase Console (Authentication ->
 * Templates -> Customize action URL on each template) should all point
 * here, e.g. https://portal.neighbortopia.app/auth/action
 *
 * Firebase appends ?mode=<mode>&oobCode=<code>&apiKey=...&lang=...
 */
function AuthActionPage() {
  const [params] = useSearchParams()
  const mode = params.get('mode')
  const oobCode = params.get('oobCode')
  const rawContinueUrl = params.get('continueUrl')
  // Ignore continueUrls that point back at the action handler itself —
  // following them creates a redirect loop into "Something went wrong"
  // because the handler needs mode/oobCode to do anything.
  const continueUrl = (() => {
    if (!rawContinueUrl) return null
    try {
      const parsed = new URL(rawContinueUrl, window.location.origin)
      if (parsed.pathname === '/auth/action') return null
      return rawContinueUrl
    } catch (_) {
      return rawContinueUrl
    }
  })()

  const [status, setStatus] = useState('loading') // loading | success | error | needs-password
  const [errorMessage, setErrorMessage] = useState('')
  const [info, setInfo] = useState({}) // mode-specific info we got from checkActionCode
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!mode || !oobCode) {
      setStatus('error')
      setErrorMessage('This link is missing required information. Please use the link from your email exactly as it was sent.')
      return
    }
    runAction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, oobCode])

  async function runAction() {
    try {
      switch (mode) {
        case 'verifyEmail':
        case 'verifyAndChangeEmail': {
          // First check the code so we can show the new email if available
          let email
          try {
            const result = await checkActionCode(auth, oobCode)
            email = result?.data?.email
          } catch (_) {
            // Non-fatal — applyActionCode below will throw with the real reason
          }

          // For email-change links, ask our backend if the user has
          // hit "Cancel Verification" since this link was sent. Firebase
          // doesn't let us invalidate oobCodes directly, so we keep our
          // own cancellation list in Firestore and refuse cancelled
          // codes here before applying.
          if (mode === 'verifyAndChangeEmail') {
            try {
              const functions = getFunctions()
              const check = httpsCallable(functions, 'checkEmailChangeCancelled')
              const checkResult = await check({ oobCode })
              if (checkResult?.data?.cancelled) {
                setStatus('error')
                setErrorMessage(
                  "This email change was cancelled from the account it belongs to. The link is no longer valid. If you didn't request the cancellation, sign in and start the change again."
                )
                return
              }
            } catch (cancelCheckErr) {
              // If the check itself fails, fall through to applyActionCode —
              // we'd rather complete a legitimate change than block on a
              // backend hiccup.
              console.warn(
                'checkEmailChangeCancelled failed; proceeding with apply:',
                cancelCheckErr
              )
            }
          }

          await applyActionCode(auth, oobCode)
          setInfo({ email })
          setStatus('success')
          return
        }

        case 'recoverEmail': {
          // Revert an email change. Tell the user the address has been restored.
          let restoredEmail
          try {
            const result = await checkActionCode(auth, oobCode)
            restoredEmail = result?.data?.email
          } catch (_) {}
          await applyActionCode(auth, oobCode)
          setInfo({ email: restoredEmail })
          setStatus('success')
          return
        }

        case 'resetPassword': {
          // Verify the code first; if good, show the new-password form.
          const email = await verifyPasswordResetCode(auth, oobCode)
          setInfo({ email })
          setStatus('needs-password')
          return
        }

        default:
          setStatus('error')
          setErrorMessage('This action link uses an unrecognized format. If you keep seeing this, contact support.')
      }
    } catch (err) {
      handleFirebaseError(err)
    }
  }

  function handleFirebaseError(err) {
    const code = err?.code || ''
    let message
    switch (code) {
      case 'auth/expired-action-code':
        message = 'This link has expired. Please request a new one and try again.'
        break
      case 'auth/invalid-action-code':
        message = 'This link is invalid or has already been used. If you already verified, you can ignore this page.'
        break
      case 'auth/user-disabled':
        message = 'This account has been disabled. Please contact support.'
        break
      case 'auth/user-not-found':
        message = "We couldn't find an account for this link."
        break
      case 'auth/weak-password':
        message = 'That password is too weak. Please choose a stronger one (at least 6 characters).'
        break
      default:
        message = err?.message || 'Something went wrong. Please request a new link and try again.'
    }
    setStatus('error')
    setErrorMessage(message)
  }

  async function submitPasswordReset(event) {
    event.preventDefault()
    if (newPassword.length < 6) {
      setErrorMessage('Please choose a password with at least 6 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("Those passwords don't match.")
      return
    }
    setErrorMessage('')
    setSubmitting(true)
    try {
      await confirmPasswordReset(auth, oobCode, newPassword)
      setStatus('success')
    } catch (err) {
      handleFirebaseError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-action-page">
      <div className="auth-action-bg">
        <div className="auth-action-gradient" />
        <div className="auth-action-pattern" />
      </div>

      <div className="auth-action-container">
        <a href="https://neighbortopia.app" className="auth-action-brand">
          <img
            src="/logo.jpeg"
            alt="NeighborTopia"
            className="auth-action-logo"
          />
          <h1>NeighborTopia</h1>
          <p>Building Stronger Neighborhoods</p>
        </a>

        <div className="auth-action-card">
          {status === 'loading' && <LoadingState />}
          {status === 'success' && <SuccessState mode={mode} info={info} continueUrl={continueUrl} />}
          {status === 'error' && <ErrorState message={errorMessage} />}
          {status === 'needs-password' && (
            <PasswordResetForm
              email={info.email}
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              onSubmit={submitPasswordReset}
              submitting={submitting}
              errorMessage={errorMessage}
            />
          )}
        </div>

        <AppStoreBadges />

        <p className="auth-action-footer">
          &copy; {new Date().getFullYear()} NeighborTopia. All rights reserved.
        </p>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="auth-action-state">
      <Loader className="auth-action-icon spin" size={48} />
      <h2>Working on it…</h2>
      <p>Verifying your link, this should only take a second.</p>
    </div>
  )
}

function SuccessState({ mode, info, continueUrl }) {
  let title
  let body
  switch (mode) {
    case 'verifyEmail':
      title = 'Email verified'
      body = info.email
        ? `${info.email} is now verified. You can close this page and return to NeighborTopia.`
        : 'Your email address has been verified. You can close this page and return to NeighborTopia.'
      break
    case 'verifyAndChangeEmail':
      title = 'Email updated'
      body = info.email
        ? `Your sign-in email is now ${info.email}. You may need to sign in again with your new email.`
        : 'Your sign-in email has been updated. You may need to sign in again with your new email.'
      break
    case 'recoverEmail':
      title = 'Email restored'
      body = info.email
        ? `Your sign-in email has been restored to ${info.email}. We've also revoked any active sessions, so be sure to sign in again and update your password.`
        : "Your sign-in email has been restored. We've also revoked any active sessions, so be sure to sign in again and update your password."
      break
    case 'resetPassword':
      title = 'Password updated'
      body = 'Your password has been changed. You can now sign in with your new password.'
      break
    default:
      title = 'All set'
      body = 'You can close this page.'
  }

  return (
    <div className="auth-action-state">
      <CheckCircle className="auth-action-icon success" size={48} />
      <h2>{title}</h2>
      <p>{body}</p>

      <div className="auth-action-buttons">
        {continueUrl ? (
          <a href={continueUrl} className="auth-action-btn primary">
            Continue
          </a>
        ) : (
          <Link to="/login" className="auth-action-btn primary">
            Sign in
          </Link>
        )}
      </div>
    </div>
  )
}

function ErrorState({ message }) {
  return (
    <div className="auth-action-state">
      <AlertTriangle className="auth-action-icon error" size={48} />
      <h2>Something went wrong</h2>
      <p>{message}</p>

      <div className="auth-action-buttons">
        <Link to="/forgot-password" className="auth-action-btn secondary">
          Request a new link
        </Link>
        <Link to="/login" className="auth-action-btn primary">
          Back to sign in
        </Link>
      </div>
    </div>
  )
}

function PasswordResetForm({
  email,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  onSubmit,
  submitting,
  errorMessage,
}) {
  return (
    <form className="auth-action-form" onSubmit={onSubmit}>
      <div className="auth-action-state compact">
        <Lock className="auth-action-icon" size={40} />
        <h2>Reset your password</h2>
        {email && (
          <p>
            for <strong>{email}</strong>
          </p>
        )}
      </div>

      <div className="auth-action-field">
        <label htmlFor="new-password">New password</label>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
        />
      </div>

      <div className="auth-action-field">
        <label htmlFor="confirm-password">Confirm new password</label>
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
        />
      </div>

      {errorMessage && (
        <div className="auth-action-form-error">
          <AlertTriangle size={16} />
          <span>{errorMessage}</span>
        </div>
      )}

      <button type="submit" className="auth-action-btn primary" disabled={submitting}>
        {submitting ? 'Updating…' : 'Update password'}
      </button>
    </form>
  )
}

function AppStoreBadges() {
  return (
    <div className="auth-action-stores">
      <a
        href="https://apps.apple.com/us/app/neighbortopia/id6752349766"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src="/app-store-badge.png"
          alt="Download on the App Store"
          className="auth-action-store-badge ios"
        />
      </a>
      <a
        href="https://play.google.com/store/apps/details?id=com.curayt.neighbortopia"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
          alt="Get it on Google Play"
          className="auth-action-store-badge android"
        />
      </a>
    </div>
  )
}

export default AuthActionPage
